import React, { useCallback, useEffect, useRef, useState } from 'react';
import SoundManager from '../utils/SoundManager';
import FalloutCryoPanel from './FalloutCryoPanel';

/**
 * FALLOUT - RobCo Industries (TM) Termlink.
 * Green-phosphor vault terminal: boot sequence, the password hacking
 * minigame (memory dump, likeness feedback, bracket tricks, lockout),
 * then a Vault-Tec log browser. Pure DOM/CSS, no WebGL, no external deps.
 * All styles scoped under `.fallout-term` so nothing leaks into the C64 theme.
 */

type Phase = 'boot' | 'hack' | 'unlocking' | 'menu' | 'entry' | 'cryo' | 'locked';

const GARBAGE = '!@#$%^&*()_+-={}[]|\\:;"\'<>,.?/';
const CLOSERS: Record<string, string> = { '(': ')', '[': ']', '{': '}', '<': '>' };

const ROWS = 16;        // lines per column
const LINE_LEN = 12;    // characters per line
const TOTAL = ROWS * 2 * LINE_LEN;
const WORD_COUNT = 9;
const MAX_ATTEMPTS = 4;
const LOCKOUT_SECONDS = 10;

const WORD_POOL = [
  'FALLOUT', 'KAMPALA', 'OKAYAMA', 'SUMMITS', 'MISSION', 'BUNKERS',
  'RADIANT', 'VAULTED', 'NUCLEAR', 'ISOTOPE', 'MUTANTS', 'LIBERTY',
  'RESCUED', 'GEIGERS', 'CARAVAN', 'SHELTER', 'REACTOR', 'ANOMALY',
  'PROTECT', 'SIGNALS', 'RATIONS', 'HOLDOUT',
];

const BOOT_LINES = [
  'ROBCO INDUSTRIES (TM) TERMLINK PROTOCOL',
  'INITIALIZING ROBCO INDUSTRIES(TM) MF BOOT AGENT v2.3.0',
  'RETROS BIOS',
  'RBIOS-4.02.08.00 52EE5.E7.E8',
  'COPYRIGHT 2201-2203 ROBCO INDUSTRIES',
  'UPPMEM DRIVE INITIALIZED',
  '',
  'MEMORY BANK 1 ....................... OK',
  'MEMORY BANK 2 ....................... OK',
  'HOLOTAPE DRIVE .................. STANDBY',
  'GEIGER ARRAY .................. 0.04 RAD/S',
  'DOOR SERVO .................... [ LOCKED ]',
  '',
  'SET TERMINAL/INQUIRE',
  'TERMINAL - VAULT 4000 / OVERSEER SUBSYSTEM',
  '',
  'SET FILE/PROTECTION=OWNER:RWED ACCOUNTS.F',
  'SET HALT RESTART/MAINT',
  '',
  'RUN DEBUG/ACCOUNTS.F',
];

interface Token {
  kind: 'word' | 'bracket';
  id: number;
}

interface WordSlot {
  id: number;
  text: string;
  start: number;
}

interface BracketSlot {
  id: number;
  start: number;
  end: number;
}

interface Board {
  chars: string[];
  tokens: (Token | null)[];
  words: WordSlot[];
  brackets: BracketSlot[];
  answerId: number;
  baseAddr: number;
}

function randomOf<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeBoard(): Board {
  const chars: string[] = [];
  for (let i = 0; i < TOTAL; i++) chars.push(GARBAGE[Math.floor(Math.random() * GARBAGE.length)]);

  const tokens: (Token | null)[] = new Array(TOTAL).fill(null);

  // --- Words: scattered through the dump, never touching each other ---
  const shuffled = [...WORD_POOL].sort(() => Math.random() - 0.5).slice(0, WORD_COUNT);
  const words: WordSlot[] = [];

  shuffled.forEach((text) => {
    for (let attempt = 0; attempt < 300; attempt++) {
      const start = Math.floor(Math.random() * (TOTAL - text.length));
      let free = true;
      for (let j = start - 1; j <= start + text.length; j++) {
        if (j >= 0 && j < TOTAL && tokens[j]) { free = false; break; }
      }
      if (!free) continue;

      const id = words.length;
      for (let j = 0; j < text.length; j++) {
        chars[start + j] = text[j];
        tokens[start + j] = { kind: 'word', id };
      }
      words.push({ id, text, start });
      return;
    }
  });

  // --- Bracket sequences: one per line at most, garbage only in between ---
  const brackets: BracketSlot[] = [];
  const lineCount = ROWS * 2;
  for (let line = 0; line < lineCount; line++) {
    const base = line * LINE_LEN;
    let placed = false;
    for (let i = 0; i < LINE_LEN - 1 && !placed; i++) {
      const closer = CLOSERS[chars[base + i]];
      if (!closer || tokens[base + i]) continue;
      for (let j = i + 1; j < LINE_LEN; j++) {
        if (tokens[base + j]) break;
        if (chars[base + j] === closer) {
          const id = brackets.length;
          for (let k = base + i; k <= base + j; k++) tokens[k] = { kind: 'bracket', id };
          brackets.push({ id, start: base + i, end: base + j });
          placed = true;
          break;
        }
      }
    }
  }

  return {
    chars,
    tokens,
    words,
    brackets,
    answerId: words.length ? randomOf(words).id : 0,
    baseAddr: 0xf000 + Math.floor(Math.random() * 0x600) * 0x10,
  };
}

function likeness(a: string, b: string): number {
  let n = 0;
  for (let i = 0; i < Math.min(a.length, b.length); i++) if (a[i] === b[i]) n++;
  return n;
}

// ===================== Vault-Tec archive =====================

interface LogEntry {
  id: string;
  label: string;
  body: string[];
  secure?: boolean;
  action?: 'door' | 'logout' | 'cryo';
}

const ENTRIES: LogEntry[] = [
  {
    id: 'file',
    label: 'PERSONNEL FILE 4000-GB',
    body: [
      'VAULT-TEC PERSONNEL FILE 4000-GB',
      '----------------------------------------',
      'DESIGNATION ....... BOURGOGNE, G.',
      'ROLE .............. FIELD OPERATOR / BUILDER',
      'CLEARANCE ......... OVERSEER-ADJACENT',
      'STATUS ............ ACTIVE - RESTLESS',
      '',
      'PSYCH EVALUATION:',
      '  Subject builds systems where none existed.',
      '  Subject walks uphill on purpose.',
      '  Subject does not delegate the difficult part.',
      '',
      'MEDICAL NOTE:',
      '  Recommend subject sleep.',
      '  Recommendation logged. Recommendation ignored.',
    ],
  },
  {
    id: 'relief',
    label: 'LOG 001 - RELIEF OPERATIONS',
    body: [
      '> PLAYING HOLOTAPE 001 ...',
      '',
      '3CAUSES - NON-GOVERNMENTAL RELIEF UNIT',
      '',
      'MANDATE: bridge those who have means and those',
      'who need action. No dependency. No theatre.',
      '',
      'FIELD DOCTRINE:',
      '  1. Listen deeply before spending anything.',
      '  2. Fund the people already doing the work.',
      '  3. Leave the structure standing when you go.',
      '',
      'SECTORS ........... LEBANON / UGANDA / FRANCE',
      'STATUS ............ OPERATIONAL',
      '',
      '> END OF TAPE',
    ],
  },
  {
    id: 'rotary',
    label: 'LOG 002 - CEDARS DISTRICT',
    body: [
      '> PLAYING HOLOTAPE 002 ...',
      '',
      'ROTARY BEIRUT CEDARS - DISTRICT 2452',
      '',
      'A city that has been rebuilt more times than it',
      'has been counted. The people did the rebuilding.',
      'We only carried water and paperwork.',
      '',
      'ASSETS DEPLOYED ... MEDICAL / SCHOOLING / WATER',
      'CASUALTY REPORT ... NOT OURS TO PUBLISH',
      'MORALE ............ STUBBORN',
      '',
      '> END OF TAPE',
    ],
  },
  {
    id: 'summits',
    label: 'LOG 003 - ALTITUDE SURVEY',
    body: [
      '> PLAYING HOLOTAPE 003 ...',
      '',
      'ALTITUDE SURVEY - HIGH GROUND RECON',
      '',
      '  KILIMANJARO ..... 5895 M ....... SUMMITED',
      '  MT STANLEY ...... 5109 M ....... SUMMITED',
      '  MT ARARAT ....... 4400 / 5137 M . PARTIAL',
      '  PEAK LENIN ...... 6400 / 7134 M . TURNED BACK',
      '',
      'OPERATOR NOTE:',
      '  The mountain does not negotiate. It only',
      '  tells you the truth about your preparation.',
      '',
      '  PEAK LENIN remains an open file.',
      '',
      '> END OF TAPE',
    ],
  },
  {
    id: 'outpost',
    label: 'LOG 004 - REMOTE OUTPOST',
    body: [
      '> PLAYING HOLOTAPE 004 ...',
      '',
      'REMOTE OUTPOST - COASTAL SECTOR, UNLISTED',
      '',
      'STRUCTURE ......... TIMBER, PRE-WAR, STANDING',
      'ROOF .............. TILE. HOLDING. MOSTLY.',
      'NEIGHBOURS ........ FEW. PATIENT. OBSERVANT.',
      'GRID REFERENCE .... WITHHELD BY OPERATOR',
      '',
      'ようこそ。 ゆっくりしていってください。',
      '',
      'MAINTENANCE BACKLOG:',
      '  - the shutters',
      '  - the garden that has opinions',
      '  - the silence, which needs none',
      '',
      '> END OF TAPE',
    ],
  },
  {
    id: 'broadcast',
    label: 'LOG 005 - EMERGENCY BROADCAST',
    body: [
      '> TUNING EMERGENCY BAND ...',
      '',
      'DISASTER FIELD GUIDE - STANDING BROADCAST',
      '',
      'IF YOU ARE HEARING THIS, YOU ARE THE RESPONSE.',
      '',
      '  1. SECURE YOURSELF FIRST. A SECOND CASUALTY',
      '     HELPS NOBODY.',
      '  2. WATER, SHELTER, INFORMATION - IN THAT ORDER.',
      '  3. WRITE DOWN WHAT YOU DID. SOMEONE AFTER YOU',
      '     WILL NEED IT.',
      '',
      'BROADCAST REPEATS INDEFINITELY.',
      '',
      '> SIGNAL HELD',
    ],
  },
  {
    id: 'cryo',
    label: '[ CRYO SUBSYSTEM - SLEEPER ROSTER ]',
    action: 'cryo',
    body: [],
  },
  {
    id: 'door',
    label: '[ RELEASE DOOR SERVO - VAULT 4000 ]',
    action: 'door',
    body: [],
  },
  {
    id: 'overseer',
    label: '[ OVERSEER PRIVATE LOG - RESTRICTED ]',
    secure: true,
    body: [
      '> DECRYPTING ...',
      '',
      'OVERSEER PRIVATE LOG - FINAL ENTRY',
      '',
      'The experiment was never the vault.',
      'The experiment was whether anyone would keep',
      'building once the doors were sealed.',
      '',
      'They did. Quietly. Without funding. Without',
      'anyone watching. That was the whole test.',
      '',
      'Log closed. Door released. Go outside.',
      '',
      '> END OF FILE',
    ],
  },
  {
    id: 'logout',
    label: '[ LOG OUT ]',
    action: 'logout',
    body: [],
  },
];

const DOOR_SEQUENCE = [
  'MAINTENANCE OVERRIDE ACCEPTED',
  'DISENGAGING SERVO LOCKS ......... 1 OF 4',
  'DISENGAGING SERVO LOCKS ......... 2 OF 4',
  'DISENGAGING SERVO LOCKS ......... 3 OF 4',
  'DISENGAGING SERVO LOCKS ......... 4 OF 4',
  'PRESSURE EQUALIZED',
  'DOOR SERVO ..................... [ OPEN ]',
  '',
  'RESTRICTED FILE UNSEALED - SEE MAIN MENU',
];

// ===================== Component =====================

const FalloutVisualization: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('boot');
  const [bootIdx, setBootIdx] = useState(0);

  const [board, setBoard] = useState<Board>(() => makeBoard());
  const [attempts, setAttempts] = useState(MAX_ATTEMPTS);
  const [duds, setDuds] = useState<number[]>([]);
  const [usedBrackets, setUsedBrackets] = useState<number[]>([]);
  const [log, setLog] = useState<string[]>([]);
  const [hover, setHover] = useState<string | null>(null);
  const [lockCountdown, setLockCountdown] = useState(LOCKOUT_SECONDS);

  const [unlockIdx, setUnlockIdx] = useState(0);
  const [menuIdx, setMenuIdx] = useState(0);
  const [entry, setEntry] = useState<LogEntry | null>(null);
  const [revealed, setRevealed] = useState(0);
  const [doorOpen, setDoorOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const bootRef = useRef<HTMLDivElement>(null);

  const beep = (name: string) => {
    try { SoundManager.playSound(name); } catch { /* no-op */ }
  };

  const pushLog = useCallback((lines: string[]) => {
    setLog((prev) => [...prev, ...lines].slice(-40));
  }, []);

  const menuItems = ENTRIES.filter((e) => !e.secure || doorOpen);
  const unlockLines = [
    'PASSWORD ACCEPTED',
    'LOGGING IN AS OVERSEER ...',
    'WELCOME TO ROBCO TERMLINK',
  ];

  // ---- Boot sequence ----
  useEffect(() => {
    if (phase !== 'boot') return;
    if (bootIdx >= BOOT_LINES.length) {
      const t = setTimeout(() => setPhase('hack'), 600);
      return () => clearTimeout(t);
    }
    const delay = BOOT_LINES[bootIdx] === '' ? 70 : 130 + Math.random() * 120;
    const t = setTimeout(() => {
      if (BOOT_LINES[bootIdx] !== '') beep('keypress');
      setBootIdx((i) => i + 1);
    }, delay);
    return () => clearTimeout(t);
  }, [phase, bootIdx, pushLog]);

  useEffect(() => {
    bootRef.current?.scrollTo({ top: bootRef.current.scrollHeight });
  }, [bootIdx]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [log]);

  // ---- Lockout countdown ----
  useEffect(() => {
    if (phase !== 'locked') return;
    if (lockCountdown <= 0) {
      setBoard(makeBoard());
      setAttempts(MAX_ATTEMPTS);
      setDuds([]);
      setUsedBrackets([]);
      setLog(['>SYSTEM REBOOT', 'Lockout cleared.']);
      setLockCountdown(LOCKOUT_SECONDS);
      setPhase('hack');
      return;
    }
    const t = setTimeout(() => setLockCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, lockCountdown]);

  // ---- Login reveal ----
  useEffect(() => {
    if (phase !== 'unlocking') return;
    if (unlockIdx >= unlockLines.length) {
      const t = setTimeout(() => setPhase('menu'), 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      beep('command');
      setUnlockIdx((i) => i + 1);
    }, 700);
    return () => clearTimeout(t);
  }, [phase, unlockIdx, unlockLines.length]);

  // ---- Entry typewriter ----
  useEffect(() => {
    if (phase !== 'entry' || !entry) return;
    if (revealed >= entry.body.length) return;
    const t = setTimeout(() => {
      if (entry.body[revealed] !== '') beep('keypress');
      setRevealed((r) => r + 1);
    }, entry.body[revealed] === '' ? 60 : 110);
    return () => clearTimeout(t);
  }, [phase, entry, revealed]);

  // ---- Keep focus for keyboard control ----
  useEffect(() => {
    rootRef.current?.focus();
  }, [phase]);

  // ===================== Hacking actions =====================

  const guessWord = (id: number) => {
    if (phase !== 'hack' || duds.includes(id)) return;
    const word = board.words.find((w) => w.id === id);
    const answer = board.words.find((w) => w.id === board.answerId);
    if (!word || !answer) return;

    if (word.id === answer.id) {
      beep('command');
      pushLog([`>${word.text}`, 'Exact match!', 'Please wait', 'while system', 'is accessed.']);
      setPhase('unlocking');
      return;
    }

    const left = attempts - 1;
    setAttempts(left);
    pushLog([`>${word.text}`, 'Entry denied', `Likeness=${likeness(word.text, answer.text)}`]);

    if (left <= 0) {
      beep('error');
      setPhase('locked');
      setLockCountdown(LOCKOUT_SECONDS);
    } else {
      beep('error');
    }
  };

  const pullBracket = (id: number) => {
    if (phase !== 'hack' || usedBrackets.includes(id)) return;
    const seq = board.brackets.find((b) => b.id === id);
    if (!seq) return;
    setUsedBrackets((prev) => [...prev, id]);

    const text = board.chars.slice(seq.start, seq.end + 1).join('');
    const remaining = board.words.filter((w) => w.id !== board.answerId && !duds.includes(w.id));

    // Replenish is the rare payoff, dud removal the common one.
    if (Math.random() < 0.25 || remaining.length === 0) {
      beep('command');
      setAttempts(MAX_ATTEMPTS);
      pushLog([`>${text}`, 'Allowance', 'replenished.']);
      return;
    }

    const dud = randomOf(remaining);
    setDuds((prev) => [...prev, dud.id]);
    beep('command');
    pushLog([`>${text}`, 'Dud removed.']);
  };

  // ===================== Menu actions =====================

  const openMenuItem = (item: LogEntry) => {
    if (item.action === 'logout') {
      beep('toggle');
      setBoard(makeBoard());
      setAttempts(MAX_ATTEMPTS);
      setDuds([]);
      setUsedBrackets([]);
      setUnlockIdx(0);
      setMenuIdx(0);
      setDoorOpen(false);
      setLog(['SESSION TERMINATED', 'ROBCO INDUSTRIES (TM) TERMLINK', 'ENTER PASSWORD NOW', '']);
      setPhase('hack');
      return;
    }
    if (item.action === 'cryo') {
      beep('command');
      setPhase('cryo');
      return;
    }
    if (item.action === 'door') {
      beep('command');
      setDoorOpen(true);
      setEntry({ ...item, body: DOOR_SEQUENCE });
      setRevealed(0);
      setPhase('entry');
      return;
    }
    beep('command');
    setEntry(item);
    setRevealed(0);
    setPhase('entry');
  };

  const backToMenu = () => {
    beep('toggle');
    setEntry(null);
    setRevealed(0);
    setPhase('menu');
  };

  // ===================== Keyboard =====================

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (phase === 'menu') {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        beep('keypress');
        setMenuIdx((i) => (i + 1) % menuItems.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        beep('keypress');
        setMenuIdx((i) => (i - 1 + menuItems.length) % menuItems.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = menuItems[menuIdx];
        if (item) openMenuItem(item);
      }
      return;
    }

    if (phase === 'cryo') {
      if (e.key === 'Backspace' || e.key === 'ArrowLeft') {
        e.preventDefault();
        backToMenu();
      }
      return;
    }

    if (phase === 'entry') {
      if (e.key === 'Backspace' || e.key === 'ArrowLeft' || e.key === 'Enter') {
        e.preventDefault();
        if (entry && revealed < entry.body.length) setRevealed(entry.body.length);
        else backToMenu();
      }
      return;
    }

    if (phase === 'hack') {
      const live = board.words.filter((w) => !duds.includes(w.id));
      if (!live.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const cur = live.findIndex((w) => hover === `w:${w.id}`);
        const next = live[(cur + 1) % live.length];
        setHover(`w:${next.id}`);
        beep('keypress');
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const cur = live.findIndex((w) => hover === `w:${w.id}`);
        const prev = live[(cur - 1 + live.length) % live.length];
        setHover(`w:${prev.id}`);
        beep('keypress');
      } else if (e.key === 'Enter' && hover?.startsWith('w:')) {
        e.preventDefault();
        guessWord(Number(hover.slice(2)));
      }
    }
  };

  // ===================== Rendering helpers =====================

  const tokenKeyAt = (i: number): string | null => {
    const t = board.tokens[i];
    if (!t) return null;
    if (t.kind === 'word' && duds.includes(t.id)) return null;
    if (t.kind === 'bracket' && usedBrackets.includes(t.id)) return null;
    return `${t.kind === 'word' ? 'w' : 'b'}:${t.id}`;
  };

  const charAt = (i: number): string => {
    const t = board.tokens[i];
    if (t && t.kind === 'word' && duds.includes(t.id)) return '.';
    return board.chars[i];
  };

  const hoverText = (): string => {
    if (!hover) return '';
    const [kind, rawId] = hover.split(':');
    const id = Number(rawId);
    if (kind === 'w') return board.words.find((w) => w.id === id)?.text ?? '';
    const seq = board.brackets.find((b) => b.id === id);
    return seq ? board.chars.slice(seq.start, seq.end + 1).join('') : '';
  };

  const renderLine = (line: number) => {
    const base = line * LINE_LEN;
    const addr = (board.baseAddr + line * LINE_LEN).toString(16).toUpperCase().padStart(4, '0');
    return (
      <div className="fo-line" key={line}>
        <span className="fo-addr">0x{addr}</span>
        <span className="fo-cells">
          {Array.from({ length: LINE_LEN }, (_, c) => {
            const i = base + c;
            const key = tokenKeyAt(i);
            const active = key !== null && key === hover;
            return (
              <span
                key={c}
                className={`fo-ch${key ? ' fo-tok' : ''}${active ? ' fo-hl' : ''}`}
                onMouseEnter={() => setHover(key)}
                onClick={() => {
                  if (!key) return;
                  const [kind, rawId] = key.split(':');
                  if (kind === 'w') guessWord(Number(rawId));
                  else pullBracket(Number(rawId));
                }}
              >
                {charAt(i)}
              </span>
            );
          })}
        </span>
      </div>
    );
  };

  // ===================== Views =====================

  const header = phase === 'boot' ? null : (
    <div className="fo-topbox">
      <span className="fo-boxtitle">Welcome to ROBCO Industries (TM) Termlink</span>
    </div>
  );

  const statusRight =
    phase === 'locked' ? 'Terminal Locked'
      : phase === 'unlocking' ? 'Password Accepted'
        : phase === 'hack' ? 'Password Required'
          : phase === 'cryo' ? 'Cryo Subsystem'
            : 'Vault 4000 / Overseer Subsystem';

  const statusRow = phase === 'boot' ? null : (
    <div className="fo-status">
      <span className="fo-attempts">
        {phase === 'hack' || phase === 'unlocking' || phase === 'locked' ? (
          <>
            Attempts Remaining:{' '}
            {Array.from({ length: MAX_ATTEMPTS }, (_, i) => (
              <i key={i} className={`fo-block${i < attempts ? '' : ' fo-block-off'}`} />
            ))}
          </>
        ) : (
          <>Door Servo: {doorOpen ? '[ OPEN ]' : '[ LOCKED ]'} · Geiger 0.04 rad/s</>
        )}
      </span>
      <span className="fo-status-right">{statusRight}</span>
    </div>
  );

  let view: React.ReactNode = null;

  if (phase === 'boot') {
    view = (
      <div className="fo-boot" ref={bootRef}>
        {BOOT_LINES.slice(0, bootIdx).map((l, i) => (
          <div className="fo-bline" key={i}>{l || ' '}</div>
        ))}
        <span className="fo-cursor">█</span>
      </div>
    );
  } else if (phase === 'locked') {
    view = (
      <div className="fo-locked">
        <div className="fo-locked-big">TERMINAL LOCKED</div>
        <div className="fo-locked-sub">PLEASE CONTACT AN ADMINISTRATOR</div>
        <div className="fo-locked-count">
          LOCKOUT RELEASE IN {String(lockCountdown).padStart(2, '0')}
        </div>
      </div>
    );
  } else if (phase === 'hack' || phase === 'unlocking') {
    view = (
      <div className="fo-hackwrap">
        <div className="fo-dump" onMouseLeave={() => setHover(null)}>
          <div className="fo-col">{Array.from({ length: ROWS }, (_, r) => renderLine(r))}</div>
          <div className="fo-col">{Array.from({ length: ROWS }, (_, r) => renderLine(ROWS + r))}</div>

          <div className="fo-side" ref={logRef}>
            <div className="fo-logwrap">
              {log.map((l, i) => (
                <div className="fo-logline" key={i}>{l || ' '}</div>
              ))}
              {phase === 'unlocking' && (
                <div className="fo-logline fo-ok">
                  {unlockLines.slice(0, unlockIdx).map((l, i) => <div key={i}>{l}</div>)}
                </div>
              )}
            </div>
            <div className="fo-prompt">&gt;{hoverText()}<span className="fo-cursor">█</span></div>
          </div>
        </div>

        <div className="fo-hint">
          Hover a word and click to try it. Matching bracket pairs remove duds or replenish attempts. Arrows + Enter also work.
        </div>
      </div>
    );
  } else if (phase === 'menu') {
    view = (
      <div className="fo-menu">
        <div className="fo-menu-head">Welcome, Overseer. {menuItems.length} files available.</div>
        <div className="fo-menu-list">
          {menuItems.map((item, i) => (
            <div
              key={item.id}
              className={`fo-menu-item${i === menuIdx ? ' fo-menu-sel' : ''}${item.secure ? ' fo-menu-secure' : ''}`}
              onMouseEnter={() => setMenuIdx(i)}
              onClick={() => openMenuItem(item)}
            >
              <span className="fo-bullet">{i === menuIdx ? '>' : ' '}</span> {item.label}
            </div>
          ))}
        </div>
        <div className="fo-hint">Arrows to move. Enter to open. Click also works.</div>
      </div>
    );
  } else if (phase === 'cryo') {
    view = <FalloutCryoPanel onBack={backToMenu} />;
  } else if (phase === 'entry' && entry) {
    view = (
      <div className="fo-entry" onClick={() => (revealed < entry.body.length ? setRevealed(entry.body.length) : backToMenu())}>
        <div className="fo-entry-body">
          {entry.body.slice(0, revealed).map((l, i) => (
            <div className="fo-eline" key={i}>{l || ' '}</div>
          ))}
          {revealed < entry.body.length && <span className="fo-cursor">█</span>}
        </div>
        <div className="fo-back" onClick={backToMenu}>[ BACK ]</div>
      </div>
    );
  }

  return (
    <div
      className={`fallout-term${phase === 'locked' ? ' fo-alarm' : ''}`}
      ref={rootRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onClick={() => rootRef.current?.focus()}
    >
      <style>{SCOPED_CSS}</style>
      <div className="fo-screen">
        {header}
        {statusRow}
        {view}
      </div>
      <div className="fo-scan" />
      <div className="fo-glass" />
    </div>
  );
};

const SCOPED_CSS = `
.fallout-term {
  --fo:      #24f56a;
  --fo-hi:   #a8ffc6;
  --fo-lo:   #128f39;
  --fo-dim:  #06491c;
  --fo-red:  #ff4d3d;
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: radial-gradient(115% 115% at 50% 42%, #052a11 0%, #021206 58%, #000402 100%);
  color: var(--fo);
  font-family: ui-monospace, 'Cascadia Mono', Consolas, 'Courier New', monospace;
  font-size: clamp(0.62rem, 1.85vw, 1rem);
  font-weight: 500;
  line-height: 1.62;
  letter-spacing: 0.07em;
  overflow: hidden;
  outline: none;
  cursor: default;
  text-shadow:
    0 0 2px rgba(36,245,106,0.9),
    0 0 8px rgba(36,245,106,0.55),
    0 0 22px rgba(36,245,106,0.28);
  animation: fo-flicker 6s linear infinite;
}
.fallout-term.fo-alarm { animation: fo-flicker 6s linear infinite, fo-redpulse 1.1s ease-in-out infinite; }

.fo-screen {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 18px 12px;
}

/* ---- ROBCO header box ---- */
.fo-topbox {
  flex-shrink: 0;
  border: 2px solid var(--fo);
  border-top-style: dashed;
  border-bottom-style: dashed;
  padding: 3px 10px;
  box-shadow: 0 0 10px rgba(36,245,106,0.18);
}
.fo-boxtitle { letter-spacing: 0.08em; }

.fo-status {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 20px;
}
.fo-status-right { margin-left: auto; }
.fo-attempts { display: inline-flex; align-items: center; gap: 0.45em; }
.fo-block {
  display: inline-block;
  width: 0.85em;
  height: 1.05em;
  background: currentColor;
  box-shadow: 0 0 8px rgba(36,245,106,0.7);
}
.fo-block-off { opacity: 0.12; box-shadow: none; }

/* ---- boot ---- */
.fo-boot { flex: 1; overflow-y: auto; }
.fo-bline { white-space: pre-wrap; }

/* ---- hacking ---- */
.fo-hackwrap { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 8px; }

.fo-dump {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: max-content max-content minmax(12ch, 28ch);
  justify-content: start;
  align-content: start;
  gap: 0 clamp(12px, 3.5vw, 46px);
  overflow-y: auto;
}
.fo-col { display: flex; flex-direction: column; }
.fo-line { white-space: pre; line-height: 1.85; }
.fo-addr { color: var(--fo-lo); margin-right: 1em; }
.fo-ch { display: inline-block; }
.fo-tok { cursor: pointer; }
.fo-hl {
  background: var(--fo);
  color: #021206;
  text-shadow: none;
}

.fo-side {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.fo-logwrap { margin-top: auto; }
.fo-logline { white-space: pre-wrap; }
.fo-ok { color: var(--fo-hi); }
.fo-prompt { padding-top: 2px; white-space: pre; }

.fo-hint {
  flex-shrink: 0;
  font-size: 0.85em;
  color: var(--fo-lo);
  letter-spacing: 0.08em;
  border-top: 1px solid rgba(15,138,52,0.4);
  padding-top: 6px;
}

/* ---- lockout ---- */
.fo-locked {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--fo-red);
  text-shadow: 0 0 8px rgba(255,77,61,0.7);
}
.fo-locked-big { font-size: 2.2em; font-weight: 700; letter-spacing: 0.25em; }
.fo-locked-sub { letter-spacing: 0.18em; }
.fo-locked-count { margin-top: 18px; color: var(--fo); text-shadow: inherit; opacity: 0.85; }

/* ---- menu ---- */
.fo-menu { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 6px; }
.fo-menu-head { color: var(--fo-hi); letter-spacing: 0.08em; margin-bottom: 6px; }
.fo-menu-list { flex: 1; overflow-y: auto; }
.fo-menu-item { padding: 2px 4px; cursor: pointer; white-space: pre-wrap; }
.fo-menu-sel { background: var(--fo); color: #021206; text-shadow: none; }
.fo-menu-secure { color: var(--fo-hi); }
.fo-menu-sel.fo-menu-secure { color: #021206; }
.fo-bullet { display: inline-block; width: 1ch; }

/* ---- entry ---- */
.fo-entry { flex: 1; min-height: 0; display: flex; flex-direction: column; cursor: pointer; }
.fo-entry-body { flex: 1; overflow-y: auto; }
.fo-eline { white-space: pre-wrap; }
.fo-back {
  flex-shrink: 0;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid rgba(15,138,52,0.4);
  color: var(--fo-hi);
  letter-spacing: 0.15em;
}
.fo-back:hover { background: var(--fo); color: #021206; text-shadow: none; }

.fo-cursor { animation: fo-blink 1s step-end infinite; }

/* ---- overlays ---- */
.fo-scan {
  position: absolute; inset: 0; pointer-events: none;
  background: repeating-linear-gradient(
    to bottom,
    rgba(0,0,0,0) 0px,
    rgba(0,0,0,0) 2px,
    rgba(0,0,0,0.30) 3px,
    rgba(0,0,0,0.30) 4px
  );
  mix-blend-mode: multiply;
  animation: fo-scanmove 9s linear infinite;
}
.fo-glass {
  position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(105% 90% at 50% 46%, transparent 46%, rgba(0,0,0,0.55) 82%, rgba(0,0,0,0.9) 100%),
    linear-gradient(108deg, rgba(180,255,210,0.055) 0%, transparent 34%);
  box-shadow: inset 0 0 90px rgba(0,0,0,0.85), inset 0 0 26px rgba(36,245,106,0.10);
}

@keyframes fo-flicker {
  0%,100% { opacity: 1; }
  46% { opacity: 0.98; }
  47% { opacity: 0.9; }
  48% { opacity: 1; }
  71% { opacity: 0.97; }
  72% { opacity: 0.87; }
  73% { opacity: 1; }
}
@keyframes fo-blink { 0%,50% { opacity: 1; } 50.1%,100% { opacity: 0; } }
@keyframes fo-scanmove { from { background-position-y: 0; } to { background-position-y: 100px; } }
@keyframes fo-redpulse {
  0%,100% { box-shadow: inset 0 0 40px rgba(255,77,61,0); }
  50% { box-shadow: inset 0 0 90px rgba(255,77,61,0.4); }
}

@media (max-width: 700px) {
  .fo-dump { grid-template-columns: auto auto; }
  .fo-side {
    grid-column: 1 / -1;
    border-left: none;
    border-top: 1px solid rgba(15,138,52,0.5);
    padding-left: 0;
    padding-top: 8px;
    margin-top: 8px;
    max-height: 30vh;
  }
  .fo-vault { display: none; }
  .fo-hint { font-size: 0.78em; }
}
`;

export default FalloutVisualization;
